// Helpers for the "one noyau accent per screen" rule. A bulk-add session has a
// single batch noyau (or indéterminé); these turn it into the CSS custom
// properties the bulk views read for their accent colour. Indéterminé maps to a
// neutral near-black so those screens stay black & white per the PMS golden rule.
import { NOYAU_CODE_TO_KEY } from '@/lib/word-links';
import type { NoyauCode, NoyauKey } from '@/schemas/word';
import type { CSSProperties } from 'react';

/** Noyau code → the hyphenated colour-variable stem used in styles.css. */
const CODE_TO_CSS_STEM: Record<NoyauCode, string> = {
  SGP: 'bleu-nuit',
  SPG: 'bleu-ciel',
  PSG: 'vert',
  PGS: 'jaune',
  GPS: 'rouge',
  GSP: 'rose',
};

/** The noyau colour-dot class key (e.g. "bleunuit") for a code, or null. */
export function noyauDotKey(noyau: NoyauCode | null): NoyauKey | null {
  return noyau ? NOYAU_CODE_TO_KEY[noyau] : null;
}

/**
 * Build the `--bulk-accent*` custom properties for a session's noyau. `-deep` is
 * used for solid fills behind white text (it keeps the jaune noyau, which fails
 * contrast on white, legible); `--bulk-accent` for thin/decorative fills; and
 * `--bulk-accent-tint` for subtle backgrounds. Indéterminé → neutral greys.
 */
export function accentStyle(noyau: NoyauCode | null): CSSProperties {
  if (!noyau) {
    return {
      '--bulk-accent': 'var(--color-pms-gray-900)',
      '--bulk-accent-deep': '#000',
      '--bulk-accent-tint': 'var(--color-pms-gray-100)',
    } as CSSProperties;
  }
  const stem = CODE_TO_CSS_STEM[noyau];
  return {
    '--bulk-accent': `var(--color-pms-${stem})`,
    '--bulk-accent-deep': `var(--color-pms-${stem}-deep)`,
    '--bulk-accent-tint': `var(--color-pms-${stem}-tint)`,
  } as CSSProperties;
}
